import * as React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
// constants
import { STATUS_FILTERS, TIME_FRAME_FILTERS } from 'constants/home'
// graphql
import { USERS_OR_POSITIONS_QUERY } from 'graphql/user/queries'
// types
import { IPosition, IUser } from 'types/graphql'
// utils
import get from 'lodash/get'
import sum from 'lodash/sum'

type Props = React.PropsWithChildren<unknown>

interface IContext {
  assignmentsAmount: number
  handleSetAssignmentsAmount: (value: number) => void
  handleSetIsGroupedByPlan: (value: boolean) => (afterChange: () => void) => void
  handleSetOverdueAmount: (value: number) => void
  handleSetStatusFilter: (value: string) => (afterChange: () => void) => void
  handleSetTimeFrameFilter: (value: string) => (afterChange: () => void) => void
  handleSetUserSearch: (value: string) => void
  handleSetUsersFilter: (member: IUser[] | IPosition[]) => void
  isGroupedByPlan: boolean
  overdueAmount: number
  statusFilter: string
  timeFrameFilter: string
  usersOrPositions: IUser[] | IPosition[] | any
  usersFilter: any
}

export const StrategyActionsBLContext = React.createContext<IContext>({} as any)

const StrategyActionsBL: React.FC<Props> = ({ children }) => {
  // state
  const [isGroupedByPlan, setIsGroupedByPlan] = React.useState<boolean>(true)
  const [statusFilter, setStatusFilter] = React.useState<string>(STATUS_FILTERS.OPEN)
  const [timeFrameFilter, setTimeFrameFilter] = React.useState<string>(TIME_FRAME_FILTERS.ALL)
  const [usersFilter, setUsersFilter] = React.useState([])
  const [userSearch, setUserSearch] = React.useState<string>('')

  const [assignmentsAmount, setAssignmentsAmount] = React.useState<number>(0)
  const [overdueAmount, setOverdueAmount] = React.useState<number>(0)

  const { data } = useQuery(USERS_OR_POSITIONS_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        search: userSearch
      }
    }
  })

  // methods
  const handleSetUsersFilter = (value) => setUsersFilter(value)
  const handleSetUserSearch = (value) => setUserSearch(value)

  const handleSetAssignmentsAmount = (num) => setAssignmentsAmount((state) => sum([state, num]))
  const handleSetOverdueAmount = (num) => setOverdueAmount((state) => sum([state, num]))

  const handleSetIsGroupedByPlan = (value) => (afterChange) => {
    afterChange()
    setIsGroupedByPlan(value)
    setAssignmentsAmount(0)
    setOverdueAmount(0)
  }

  const handleSetStatusFilter = (value) => (afterChange) => {
    afterChange()
    setStatusFilter(value)
  }

  const handleSetTimeFrameFilter = (value) => (afterChange) => {
    afterChange()
    setTimeFrameFilter(value)
  }

  const usersOrPositions = get(data, 'usersOrPositions.data', [])

  const value = {
    assignmentsAmount,
    handleSetAssignmentsAmount,
    handleSetIsGroupedByPlan,
    handleSetOverdueAmount,
    handleSetStatusFilter,
    handleSetTimeFrameFilter,
    handleSetUserSearch,
    handleSetUsersFilter,
    isGroupedByPlan,
    overdueAmount,
    statusFilter,
    timeFrameFilter,
    usersFilter,
    usersOrPositions
  }

  return <StrategyActionsBLContext.Provider value={value}>{children}</StrategyActionsBLContext.Provider>
}

export default StrategyActionsBL
